'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SlopeCalculator() {
  const t = useTranslations("tools.slopeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Two Points Mode State
  const [twoPointsInputs, setTwoPointsInputs] = useState({
    x1: 1,
    y1: 1,
    x2: 2,
    y2: 2
  });

  // Point + Slope Mode State
  const [pointSlopeInputs, setPointSlopeInputs] = useState({
    px1: 1,
    py1: 1,
    distance: 5,
    slope: 0.75,
    angle: ''
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [activeMode, setActiveMode] = useState<'twoPoints' | 'pointSlope'>('twoPoints');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) < 0.0001 && num !== 0) {
      return num.toExponential(4);
    }
    return (Math.round(num * 100000) / 100000).toString();
  };

  const calculateTwoPoints = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { x1, y1, x2, y2 } = twoPointsInputs;

    if (x2 === x1) {
      setError(t('errorVerticalLine'));
      setResults(null);
      return;
    }

    const slope = (y2 - y1) / (x2 - x1);
    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const angleRad = Math.atan(slope);
    const angleDeg = angleRad * (180 / Math.PI);
    const yIntercept = y1 - slope * x1;
    const rise = y2 - y1;
    const run = x2 - x1;
    const percentGrade = Math.abs(slope * 100);

    setResults({
      mode: 'twoPoints',
      slope: formatNumber(slope),
      distance: formatNumber(distance),
      angle: formatNumber(angleDeg),
      yIntercept: formatNumber(yIntercept),
      rise: formatNumber(rise),
      run: formatNumber(run),
      percentGrade: formatNumber(percentGrade),
      equation: `y = ${formatNumber(slope)}x ${yIntercept >= 0 ? '+' : ''} ${formatNumber(yIntercept)}`
    });
  };

  const calculatePointSlope = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { px1, py1, distance, slope, angle } = pointSlopeInputs;

    let m: number | string = slope;

    if (angle !== '' && angle !== null) {
      m = Math.tan(parseFloat(angle.toString()) * Math.PI / 180);
    }

    if (!Number.isFinite(m) && (angle === '' || angle === null)) {
      setError(t('errorMissingSlope'));
      setResults(null);
      return;
    }

    const mValue = typeof m === 'string' ? parseFloat(m) : m;
    const angleRad = Math.atan(mValue);
    const dx = distance / Math.sqrt(1 + mValue * mValue);
    const dy = mValue * dx;

    const x2_pos = px1 + dx;
    const y2_pos = py1 + dy;
    const x2_neg = px1 - dx;
    const y2_neg = py1 - dy;

    const yIntercept = py1 - mValue * px1;
    const angleDeg = angleRad * (180 / Math.PI);
    const percentGrade = Math.abs(mValue * 100);

    setResults({
      mode: 'pointSlope',
      slope: formatNumber(mValue),
      angle: formatNumber(angleDeg),
      x2_positive: formatNumber(x2_pos),
      y2_positive: formatNumber(y2_pos),
      x2_negative: formatNumber(x2_neg),
      y2_negative: formatNumber(y2_neg),
      yIntercept: formatNumber(yIntercept),
      percentGrade: formatNumber(percentGrade),
      equation: `y = ${formatNumber(mValue)}x ${yIntercept >= 0 ? '+' : ''} ${formatNumber(yIntercept)}`
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '900px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary, margin: 0 },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    modesGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    modeCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    modeTitle: getResponsiveStyle(
      { fontWeight: 600, color: '#2563eb', margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formRow: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginBottom: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '0.5rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 1.5rem', fontSize: '1rem' }
    ),
    errorMessage: getResponsiveStyle(
      {
        background: '#fee2e2',
        borderLeft: '4px solid #ef4444',
        borderRadius: '0.5rem',
        color: '#991b1b',
        marginTop: '1rem',
        display: error ? 'block' : 'none'
      },
      { padding: '0.875rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        marginTop: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        display: results ? 'block' : 'none'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.25rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #2563eb'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.25rem' }
    ),
    resultLabel: {
      display: 'block',
      fontSize: '0.875rem',
      color: '#64748b',
      fontWeight: 600,
      marginBottom: '0.5rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    resultValue: getResponsiveStyle(
      { display: 'block', color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultValueHighlight: {
      color: '#2563eb'
    },
    formulaDisplay: getResponsiveStyle(
      {
        background: '#fef3c7',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '0.5rem',
        marginTop: '1.5rem',
        fontFamily: "'Courier New', monospace",
        color: '#78350f'
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.95rem' },
      { padding: '1rem', fontSize: '0.95rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
            <p style={styles.toolSubtitle}>{t("toolSubtitle")}</p>
          </div>

          <div style={styles.modesGrid}>
            {/* Two Points Mode */}
            <div style={styles.modeCard}>
              <h3 style={styles.modeTitle}>📍 {t("twoPointsMode")}</h3>
              <form onSubmit={calculateTwoPoints}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="x1">{t("labelX1")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="x1"
                      step="any"
                      value={twoPointsInputs.x1}
                      onChange={(e) => setTwoPointsInputs({...twoPointsInputs, x1: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('twoPoints')}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="y1">{t("labelY1")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="y1"
                      step="any"
                      value={twoPointsInputs.y1}
                      onChange={(e) => setTwoPointsInputs({...twoPointsInputs, y1: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('twoPoints')}
                      required
                    />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="x2">{t("labelX2")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="x2"
                      step="any"
                      value={twoPointsInputs.x2}
                      onChange={(e) => setTwoPointsInputs({...twoPointsInputs, x2: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('twoPoints')}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="y2">{t("labelY2")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="y2"
                      step="any"
                      value={twoPointsInputs.y2}
                      onChange={(e) => setTwoPointsInputs({...twoPointsInputs, y2: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('twoPoints')}
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t("calculateSlope")}
                </button>
              </form>
            </div>

            {/* Point + Slope Mode */}
            <div style={styles.modeCard}>
              <h3 style={styles.modeTitle}>📊 {t("pointSlopeMode")}</h3>
              <form onSubmit={calculatePointSlope}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="px1">{t("labelX1")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="px1"
                      step="any"
                      value={pointSlopeInputs.px1}
                      onChange={(e) => setPointSlopeInputs({...pointSlopeInputs, px1: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('pointSlope')}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="py1">{t("labelY1")}</label>
                    <input
                      style={styles.input}
                      type="number"
                      id="py1"
                      step="any"
                      value={pointSlopeInputs.py1}
                      onChange={(e) => setPointSlopeInputs({...pointSlopeInputs, py1: parseFloat(e.target.value) || 0})}
                      onFocus={() => setActiveMode('pointSlope')}
                      required
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="distance">{t("labelDistance")}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="distance"
                    step="any"
                    value={pointSlopeInputs.distance}
                    onChange={(e) => setPointSlopeInputs({...pointSlopeInputs, distance: parseFloat(e.target.value) || 0})}
                    onFocus={() => setActiveMode('pointSlope')}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("labelSlopeOrAngle")}</label>
                  <div style={styles.formRow}>
                    <input
                      style={styles.input}
                      type="number"
                      step="any"
                      placeholder={t("placeholderSlope")}
                      value={pointSlopeInputs.slope}
                      onChange={(e) => setPointSlopeInputs({...pointSlopeInputs, slope: parseFloat(e.target.value) || 0, angle: ''})}
                      onFocus={() => setActiveMode('pointSlope')}
                    />
                    <input
                      style={styles.input}
                      type="number"
                      step="any"
                      placeholder={t("placeholderAngle")}
                      value={pointSlopeInputs.angle}
                      onChange={(e) => setPointSlopeInputs({...pointSlopeInputs, angle: e.target.value, slope: 0})}
                      onFocus={() => setActiveMode('pointSlope')}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t("calculatePoint")}
                </button>
              </form>
            </div>
          </div>

          {/* Error Message */}
          <div style={styles.errorMessage}>
            {error}
          </div>

          {/* Results */}
          <div style={styles.results}>
            <h3 style={styles.resultsTitle}>📊 {t("resultsTitle")}</h3>
            <div style={styles.resultGrid}>
              {results && results.mode === 'twoPoints' && (
                <>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultSlope")}</span>
                    <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{results.slope}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultDistance")}</span>
                    <span style={styles.resultValue}>{results.distance}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultAngle")}</span>
                    <span style={styles.resultValue}>{results.angle}°</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultYIntercept")}</span>
                    <span style={styles.resultValue}>{results.yIntercept}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultRise")}</span>
                    <span style={styles.resultValue}>{results.rise}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultRun")}</span>
                    <span style={styles.resultValue}>{results.run}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultPercentGrade")}</span>
                    <span style={styles.resultValue}>{results.percentGrade}%</span>
                  </div>
                </>
              )}
              {results && results.mode === 'pointSlope' && (
                <>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultSlope")}</span>
                    <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>{results.slope}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultAngle")}</span>
                    <span style={styles.resultValue}>{results.angle}°</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultPoint2Positive")}</span>
                    <span style={styles.resultValue}>({results.x2_positive}, {results.y2_positive})</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultPoint2Negative")}</span>
                    <span style={styles.resultValue}>({results.x2_negative}, {results.y2_negative})</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultYIntercept")}</span>
                    <span style={styles.resultValue}>{results.yIntercept}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("resultPercentGrade")}</span>
                    <span style={styles.resultValue}>{results.percentGrade}%</span>
                  </div>
                </>
              )}
            </div>
            {results && (
              <div style={styles.formulaDisplay}>
                <strong>{t("lineEquation")}:</strong> {results.equation}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("step1Title")}:</span> {t("step1Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step2Title")}:</span> {t("step2Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step3Title")}:</span> {t("step3Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step4Title")}:</span> {t("step4Description")}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight1Title")}:</span> {t("insight1Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight2Title")}:</span> {t("insight2Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight3Title")}:</span> {t("insight3Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight4Title")}:</span> {t("insight4Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight5Title")}:</span> {t("insight5Description")}</li>
        </ul>
      </section>

      {/* Detailed Information */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsFormula")}</h3>
        <p style={styles.paragraph}>{t("detailsFormulaDescription")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsTypes")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("detailsPositiveSlope")}:</span> {t("detailsPositiveSlopeDescription")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("detailsNegativeSlope")}:</span> {t("detailsNegativeSlopeDescription")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("detailsZeroSlope")}:</span> {t("detailsZeroSlopeDescription")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("detailsUndefinedSlope")}:</span> {t("detailsUndefinedSlopeDescription")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("detailsApplications")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("detailsApp1")}</li>
          <li style={styles.listItem}>{t("detailsApp2")}</li>
          <li style={styles.listItem}>{t("detailsApp3")}</li>
          <li style={styles.listItem}>{t("detailsApp4")}</li>
          <li style={styles.listItem}>{t("detailsApp5")}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("bestPracticesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice1Title")}:</span> {t("practice1Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice2Title")}:</span> {t("practice2Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice3Title")}:</span> {t("practice3Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice4Title")}:</span> {t("practice4Description")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice5Title")}:</span> {t("practice5Description")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.paragraph}>{t("faq1Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.paragraph}>{t("faq2Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.paragraph}>{t("faq3Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.paragraph}>{t("faq4Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.paragraph}>{t("faq5Answer")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.paragraph}>{t("faq6Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.graphcalc.com/slope-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.omnicalculator.com/math/slope"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.calculator.net/slope-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref3")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.cuemath.com/geometry/slope/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref4")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.usgs.gov/educational-resources/determine-percent-slope-and-angle-slope"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}
